<template>
  <woot-modal :show="show" :on-close="close">
    <div class="activity-modal">
      <h3 class="activity-modal__title">{{ title }}</h3>
      <form class="activity-modal__form" @submit.prevent="submit">
        <div class="activity-modal__panel">
          <div class="activity-modal__column">
            <template v-if="isRescheduling">
              <p class="activity-modal__current">
                <opportunity-activity-type-icon
                  :type="activity.activity_type"
                  size="small"
                  variant="soft"
                />
                <span>
                  {{ activityTypeLabel(activity.activity_type) }} -
                  {{
                    $t(
                      'OPPORTUNITY_DETAILS.ACTIVITIES.RESCHEDULE_MODAL.CURRENT',
                      {
                        date: currentScheduledAt,
                      }
                    )
                  }}
                </span>
              </p>
            </template>
            <template v-else>
              <label
                class="activity-modal__label"
                :class="{ error: $v.activityType.$error }"
              >
                {{ $t('OPPORTUNITY_DETAILS.ACTIVITIES.CREATE_MODAL.TYPE') }}
              </label>
              <div
                class="multiselect-wrap--small activity-modal__type"
                :class="{ error: $v.activityType.$error }"
              >
                <multiselect
                  v-model="selectedTypeOption"
                  :options="typeOptions"
                  track-by="value"
                  label="label"
                  :placeholder="
                    $t(
                      'OPPORTUNITY_DETAILS.ACTIVITIES.CREATE_MODAL.TYPE_PLACEHOLDER'
                    )
                  "
                  :searchable="false"
                  :allow-empty="false"
                  :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                  deselect-label=""
                  selected-label=""
                >
                  <template #singleLabel="{ option }">
                    <span class="activity-modal__option">
                      <opportunity-activity-type-icon
                        :type="option.value"
                        size="small"
                        variant="soft"
                      />
                      {{ option.label }}
                    </span>
                  </template>
                  <template #option="{ option }">
                    <span class="activity-modal__option">
                      <opportunity-activity-type-icon
                        :type="option.value"
                        size="small"
                        variant="soft"
                      />
                      {{ option.label }}
                    </span>
                  </template>
                </multiselect>
              </div>
              <span v-if="$v.activityType.$error" class="activity-modal__error">
                {{
                  $t(
                    'OPPORTUNITY_DETAILS.ACTIVITIES.CREATE_MODAL.ERRORS.TYPE_REQUIRED'
                  )
                }}
              </span>
            </template>

            <label
              class="activity-modal__label"
              :class="{ error: $v.scheduledAt.$error }"
            >
              {{ scheduledAtLabel }}
            </label>
            <div class="date-picker no-margin activity-modal__date">
              <date-picker
                :value="scheduledAt"
                type="datetime"
                format="DD/MM/YYYY HH:mm"
                popup-class="opportunity-activity-date-popup"
                :lang="datePickerLang"
                :placeholder="
                  $t(
                    'OPPORTUNITY_DETAILS.ACTIVITIES.CREATE_MODAL.SCHEDULED_AT_PLACEHOLDER'
                  )
                "
                :confirm="true"
                :confirm-text="
                  $t('OPPORTUNITY_DETAILS.ACTIVITIES.DATE_PICKER_CONFIRM')
                "
                :clearable="false"
                :default-value="defaultPickerValue"
                :disabled-date="isDateDisabled"
                :disabled-time="isTimeDisabled"
                @change="onScheduledAtChange"
              />
            </div>
            <span v-if="$v.scheduledAt.$error" class="activity-modal__error">
              {{ scheduledAtError }}
            </span>
            <p v-if="isRetroactive" class="activity-modal__hint">
              {{
                $t(
                  'OPPORTUNITY_DETAILS.ACTIVITIES.CREATE_MODAL.RETROACTIVE_HINT'
                )
              }}
            </p>
          </div>

          <div v-if="!isRescheduling" class="activity-modal__column">
            <label class="activity-modal__label" :for="descriptionId">
              {{
                $t('OPPORTUNITY_DETAILS.ACTIVITIES.CREATE_MODAL.DESCRIPTION')
              }}
            </label>
            <textarea
              :id="descriptionId"
              v-model="description"
              class="activity-modal__description"
              rows="5"
              :placeholder="
                $t(
                  'OPPORTUNITY_DETAILS.ACTIVITIES.CREATE_MODAL.DESCRIPTION_PLACEHOLDER'
                )
              "
            />
          </div>
        </div>

        <div class="activity-modal__footer">
          <woot-button
            data-testid="activity-modal-save"
            :is-loading="isSubmitting"
            :is-disabled="$v.$invalid || isSubmitting"
          >
            {{ saveLabel }}
          </woot-button>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import startOfDay from 'date-fns/startOfDay';
import { required } from 'vuelidate/lib/validators';
import {
  OPPORTUNITY_ACTIVITY_TYPES,
  activityDatePickerLang,
  activityTypeLabelKey,
  formatActivityDateTime,
  isPastDate,
} from 'dashboard/helper/opportunityActivityHelper';
import OpportunityActivityTypeIcon from './OpportunityActivityTypeIcon.vue';

let instanceCount = 0;

const isFutureDate = value => !value || !isPastDate(value);

const nextFullHour = () => {
  const date = new Date();
  date.setHours(date.getHours() + 1, 0, 0, 0);
  return date;
};

export default {
  name: 'OpportunityActivityModal',
  components: {
    DatePicker,
    OpportunityActivityTypeIcon,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    activity: {
      type: Object,
      default: null,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    instanceCount += 1;
    return {
      activityType: null,
      scheduledAt: null,
      description: '',
      defaultPickerValue: nextFullHour(),
      descriptionId: `opportunity-activity-description-${instanceCount}`,
    };
  },
  validations() {
    if (this.isRescheduling) {
      return { scheduledAt: { required, isFutureDate } };
    }

    return { activityType: { required }, scheduledAt: { required } };
  },
  computed: {
    isRescheduling() {
      return !!this.activity;
    },
    title() {
      return this.isRescheduling
        ? this.$t('OPPORTUNITY_DETAILS.ACTIVITIES.RESCHEDULE_MODAL.TITLE')
        : this.$t('OPPORTUNITY_DETAILS.ACTIVITIES.CREATE_MODAL.TITLE');
    },
    saveLabel() {
      return this.isRescheduling
        ? this.$t('OPPORTUNITY_DETAILS.ACTIVITIES.RESCHEDULE_MODAL.SAVE')
        : this.$t('OPPORTUNITY_DETAILS.ACTIVITIES.CREATE_MODAL.SAVE');
    },
    scheduledAtLabel() {
      return this.isRescheduling
        ? this.$t(
            'OPPORTUNITY_DETAILS.ACTIVITIES.RESCHEDULE_MODAL.SCHEDULED_AT'
          )
        : this.$t('OPPORTUNITY_DETAILS.ACTIVITIES.CREATE_MODAL.SCHEDULED_AT');
    },
    scheduledAtError() {
      if (this.isRescheduling && this.scheduledAt) {
        return this.$t(
          'OPPORTUNITY_DETAILS.ACTIVITIES.RESCHEDULE_MODAL.ERRORS.FUTURE_REQUIRED'
        );
      }

      return this.$t(
        'OPPORTUNITY_DETAILS.ACTIVITIES.CREATE_MODAL.ERRORS.SCHEDULED_AT_REQUIRED'
      );
    },
    currentScheduledAt() {
      return formatActivityDateTime(
        this.activity && this.activity.scheduled_at
      );
    },
    typeOptions() {
      return OPPORTUNITY_ACTIVITY_TYPES.map(value => ({
        value,
        label: this.activityTypeLabel(value),
      }));
    },
    selectedTypeOption: {
      get() {
        return (
          this.typeOptions.find(option => option.value === this.activityType) ||
          null
        );
      },
      set(option) {
        this.activityType = option ? option.value : null;
        this.$v.activityType.$touch();
      },
    },
    isRetroactive() {
      return !this.isRescheduling && isPastDate(this.scheduledAt);
    },
    datePickerLang() {
      return activityDatePickerLang(
        this.$root.$i18n && this.$root.$i18n.locale
      );
    },
  },
  watch: {
    show(value) {
      if (value) {
        this.resetForm();
      }
    },
  },
  methods: {
    activityTypeLabel(type) {
      return this.$t(activityTypeLabelKey(type));
    },
    resetForm() {
      this.activityType = null;
      this.scheduledAt = null;
      this.description = '';
      this.defaultPickerValue = nextFullHour();
      this.$v.$reset();
    },
    onScheduledAtChange(value) {
      this.scheduledAt = value || null;
      this.$v.scheduledAt.$touch();
    },
    isDateDisabled(date) {
      return this.isRescheduling && date < startOfDay(new Date());
    },
    isTimeDisabled(date) {
      return this.isRescheduling && date < new Date();
    },
    close() {
      this.$emit('close');
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid || this.isSubmitting) {
        return;
      }

      this.$emit('submit', {
        activityType: this.activityType,
        scheduledAt: this.scheduledAt,
        description: this.description.trim(),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.activity-modal {
  padding: var(--space-large);
}

.activity-modal__title {
  color: var(--s-900);
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-bold);
  margin: 0 var(--space-large) var(--space-medium) 0;
}

.activity-modal__form {
  padding: 0;
}

.activity-modal__panel {
  background: var(--s-25);
  border-radius: var(--border-radius-medium);
  display: grid;
  gap: var(--space-medium);
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  padding: var(--space-medium);

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.activity-modal__column {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.activity-modal__label {
  color: var(--s-900);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold);
  margin: var(--space-normal) 0 var(--space-small);

  &:first-child {
    margin-top: 0;
  }

  &.error {
    color: var(--r-500);
  }
}

.activity-modal__type ::v-deep .multiselect {
  margin-bottom: 0;
}

.activity-modal__option {
  align-items: center;
  display: inline-flex;
  gap: var(--space-small);
}

.activity-modal__type ::v-deep .multiselect__option {
  .activity-modal__option,
  .activity-type-icon {
    display: inline-flex;
    overflow: visible;
  }

  .activity-type-icon {
    height: 2.4rem;
    width: 2.4rem;
  }
}

.activity-modal__current {
  align-items: center;
  color: var(--s-800);
  display: flex;
  font-size: var(--font-size-small);
  gap: var(--space-small);
  margin: 0 0 var(--space-small);
}

.activity-modal__error {
  color: var(--r-500);
  font-size: var(--font-size-mini);
  margin-top: var(--space-smaller);
}

.activity-modal__hint {
  background: var(--y-50);
  border: 1px solid var(--y-200);
  border-radius: var(--border-radius-normal);
  color: var(--y-900);
  font-size: var(--font-size-small);
  margin: var(--space-small) 0 0;
  padding: var(--space-small) var(--space-normal);
}

.activity-modal__description {
  flex: 1;
  margin: 0;
  min-height: 12rem;
  resize: vertical;
}

.activity-modal__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: var(--space-medium);
}
</style>

<style lang="scss">
.opportunity-activity-date-popup {
  z-index: 99999 !important;
}
</style>
